import { __awaiter } from "tslib";
import { FASTElement } from '@microsoft/fast-element';
import { DI, Registration } from '@microsoft/fast-foundation';
import { HeaderConfig, defaultHeaderConfig } from './config';
/**
 * A function that allows configuring the header micro-frontend with name and templateOptions.
 *
 * @remarks
 * Configure the Header micro frontend for host app integration.
 *
 * @example Providing template options to align to your host application.
 * ```ts
 *   const { configure } = await import('@genesislcap/foundation-header/config'); // < import from the subpath
 *   return configure({
 *     name: `nexus-app-header`,
 *     // These will be merged with the packages `defaultTemplateOptions`. You will need to have these components registered in your application.
 *     templateOptions: {
 *       button: 'nexus-button',
 *       icon: 'nexus-icon-next',
 *     },
 *   });
 * },
 * ```
 *
 * @param config - A partial HeaderConfig.
 * @public
 */
export function configure(config) {
    return __awaiter(this, void 0, void 0, function* () {
        /**
         * Merge the configs
         */
        const value = Object.assign(Object.assign({}, defaultHeaderConfig), config);
        if (config.templateOptions) {
            value.templateOptions = Object.assign(Object.assign(Object.assign({}, defaultHeaderConfig.templateOptions), { provider: 'template' }), config.templateOptions);
        }
        /**
         * Register a new HeaderConfig
         */
        DI.getOrCreateDOMContainer().register(Registration.instance(HeaderConfig, value));
        /**
         * Lazily reference and define the micro frontend element post config setting.
         */
        const { Navigation, DynamicTemplate, MainStyles } = yield import('../main');
        const { name, attributes, shadowOptions, elementOptions } = value;
        return FASTElement.define(Navigation, {
            name,
            template: DynamicTemplate,
            styles: MainStyles,
            attributes,
            shadowOptions,
            elementOptions,
        });
    });
}
